import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@/components/providers/ThemeProvider';
import { BrandProvider } from '@/components/providers/BrandProvider';
import { WebSocketProvider } from '@/components/providers/WebSocketProvider';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import AppLayout from '@/components/layout/AppLayout';
import Login from '@/pages/Login';
import PublicInvestigationIntake from '@/pages/PublicInvestigationIntake';
import PublicOpsPlanView from '@/pages/PublicOpsPlanView';
import Home from '@/pages/Home';
import Settings from '@/pages/Settings';
import Profile from '@/pages/Profile';
import SearchResults from '@/pages/SearchResults';
import { legalRoutes } from '@/modules/legal/routes';
import { securityRoutes } from '@/modules/security/routes';
import { LEGACY_REDIRECTS } from '@/config/relocatedRoutes';
import { LegacyPrefixRedirect } from '@/components/routing/LegacyPrefixRedirect';
import '@/styles/globals.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on authentication errors
        if ((error as { response?: { status?: number } })?.response?.status === 401) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
    mutations: {
      retry: false,
    },
  },
});

// Placeholder components for routes that haven't been implemented yet
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="p-6">
    <div className="max-w-md mx-auto mt-20 text-center">
      <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
        {title}
      </h1>
      <p className="text-secondary-600 dark:text-secondary-400">
        This page is coming soon. We&apos;re working hard to bring you this feature.
      </p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <QueryClientProvider client={queryClient}>
          <WebSocketProvider>
          <ThemeProvider defaultTheme="system" storageKey="corporate-security-theme">
            <ErrorBoundary>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div className="min-h-screen bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white">
                  <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/submit-investigation-request"
                element={<PublicInvestigationIntake />}
              />
              <Route path="/share/ops-plan/:token" element={<PublicOpsPlanView />} />

              {/* Protected routes */}
              <Route path="/" element={<AppLayout />}>
                {/* Global home dashboard is the post-login landing (VIS-152). */}
                <Route index element={<Home />} />

                {/* Legacy global paths now redirect into the Security module so
                    existing bookmarks keep working (VIS-150, VIS-197). The splat
                    (`<from>/*`) means nested deep links redirect too, e.g.
                    /incidents/:id -> /security/incidents/:id and
                    /threats/feeds/manage -> /security/threats/feeds/manage. */}
                {LEGACY_REDIRECTS.map(({ from, to }) => (
                  <Route key={from} path={`${from}/*`} element={<LegacyPrefixRedirect to={to} />} />
                ))}

                {/* Corporate Security module — all routes live under /security/* (VIS-197). */}
                <Route path="security">
                  {securityRoutes}
                </Route>

                {/* Legal & Compliance */}
                <Route path="legal">
                  {legalRoutes}
                </Route>

                {/* Search */}
                <Route path="search" element={<SearchResults />} />

                {/* Settings & Profile */}
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                <Route path="notifications" element={<PlaceholderPage title="Notifications" />} />

                {/* Catch-all route for 404 */}
                <Route path="*" element={
                  <div className="p-6">
                    <div className="max-w-md mx-auto mt-20 text-center">
                      <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
                        Page Not Found
                      </h1>
                      <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                        The page you&apos;re looking for doesn&apos;t exist or has been moved.
                      </p>
                      <button
                        onClick={() => window.history.back()}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                } />
              </Route>
              </Routes>
                </div>
              </Router>
            </ErrorBoundary>
          </ThemeProvider>
        </WebSocketProvider>

        {/* React Query Devtools - only in development */}
        {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
          />
        )}
        </QueryClientProvider>
      </BrandProvider>
    </ErrorBoundary>
  );
}

export default App;
